import { ContentNugget } from '@backrs_ui/common-ui';

import {
  CHART_TYPES,
  ChartSeries,
  ChartSpec,
  ChartType,
  PromptItem,
  SessionStatus,
} from '@backrs_ui/grow-ui/src/models/models';

export type NuggetReference = Readonly<{
  type: string;
  id: string;
  contentNugget?: ContentNugget;
}>;

export type StreamedMessageResponse = Readonly<{
  response_id: number | undefined;
}>;

export type MetaMessage = Readonly<{
  type: 'meta';
  query_id: number | null;
  response_id: number;
}>;

export type DeltaMessage = Readonly<{
  type: 'delta';
  content: string;
}>;

export type CollegeShortlistCollege = Readonly<{
  id?: string;
  name?: string;
  relevance?: string;
  highlights?: string;
  considerations?: string;
}>;

// Mutable: built incrementally in boxData reducers.
export interface CollegeShortlistPayload {
  safe?: CollegeShortlistCollege[];
  target?: CollegeShortlistCollege[];
  stretch?: CollegeShortlistCollege[];
}

export type StudyPlanWeek = Readonly<{
  week: number;
  goals: string[];
  monday: string[];
  tuesday: string[];
  wednesday: string[];
  thursday: string[];
  friday: string[];
  saturday: string[];
  sunday: string[];
}>;

// Mutable: built incrementally in boxData reducers.
export interface StudyPlanPayload {
  priorities: string[];
  weeks: StudyPlanWeek[];
}

export type CollegeShortlistBucket = 'safe' | 'target' | 'stretch';

export type Artifact =
  | { kind: 'markdown'; payload: string }
  | { kind: 'college_shortlist'; payload: CollegeShortlistPayload }
  | { kind: 'study_plan'; payload: StudyPlanPayload };

export type ArtifactKind = Artifact['kind'];

const ARTIFACT_KINDS: ReadonlySet<ArtifactKind> = new Set<ArtifactKind>([
  'markdown',
  'college_shortlist',
  'study_plan',
]);

export const isValidArtifact = (value: unknown): value is Artifact => {
  if (!value || typeof value !== 'object') return false;

  const kind = (value as { kind?: unknown }).kind;

  return typeof kind === 'string' && ARTIFACT_KINDS.has(kind as ArtifactKind);
};

export type MarkdownDelta = Readonly<{
  append: string;
}>;

export type CollegeShortlistDelta = Readonly<{
  bucket: CollegeShortlistBucket;
  index: number;
  school: CollegeShortlistCollege;
}>;

export type StudyPlanDelta =
  | { field: 'priorities'; value: string[] }
  | { field: 'week'; index: number; value: StudyPlanWeek };

export type ArtifactMessage =
  | Readonly<{ type: 'artifact'; kind: 'markdown'; delta: MarkdownDelta }>
  | Readonly<{ type: 'artifact'; kind: 'college_shortlist'; delta: CollegeShortlistDelta }>
  | Readonly<{ type: 'artifact'; kind: 'study_plan'; delta: StudyPlanDelta }>;

export type ChartMessage = Readonly<{
  type: 'chart';
  spec: ChartSpec;
}>;

const CHART_TYPE_SET: ReadonlySet<ChartType> = new Set<ChartType>(CHART_TYPES);

const isChartSeries = (value: unknown): value is ChartSeries => {
  if (!value || typeof value !== 'object') return false;

  const { name, values } = value as Partial<ChartSeries>;

  return typeof name === 'string' && Array.isArray(values);
};

export const isChartSpec = (value: unknown): value is ChartSpec => {
  if (!value || typeof value !== 'object') return false;

  const { type, title, categories, series } = value as Partial<ChartSpec>;

  return (
    typeof type === 'string' &&
    CHART_TYPE_SET.has(type as ChartType) &&
    typeof title === 'string' &&
    Array.isArray(categories) &&
    Array.isArray(series) &&
    series.every(isChartSeries)
  );
};

export type ToolMessage = Readonly<{
  type: 'tool';
  tool_name: string;
  status: 'started' | 'completed';
  display_name: string;
}>;

export type NuggetMessage = Readonly<{
  type: 'nugget';
  nugget_type: string;
  data: Record<string, unknown>;
}>;

export type PromptMessage = Readonly<{
  type: 'prompt';
  prompt: PromptItem;
}>;

export type PlaybookMessage = Readonly<{
  type: 'playbook';
  playbook_id: number;
}>;

export type FeaturedTypeMessage = Readonly<{
  type: 'featured_type';
  nugget_type: string;
}>;

export type ComponentMessage = Readonly<{
  type: 'component';
  component_type: string;
  options?: string[];
  exclusive_options?: string[];
  include_freeform?: boolean;
  required?: boolean;
  document_type?: string;
  allow_skip?: boolean;
  users?: {
    id: string;
    relevance?: string;
    metadata?: Record<string, unknown>;
  }[];
}>;

export type FollowUpButtonPayload = Readonly<{
  id: string;
  label: string;
  prompt_button_id?: string;
  prompt?: string;
}>;

export type FollowUpButtonsMessage = Readonly<{
  type: 'follow_up_buttons';
  buttons: FollowUpButtonPayload[];
}>;

export type DoneMessage = Readonly<{
  type: 'done';
  ask_to_regenerate?: boolean;
  session_title?: string;
  session_status?: SessionStatus;
}>;

export type DoneMessageWithIds = DoneMessage &
  Readonly<{
    response: string;
    query_id: number | null;
    response_id: number | null;
    suggested_prompts: PromptItem[];
    nugget_ids: NuggetReference[];
    playbook_id: number | null;
    featured_nugget_types: string[];
    follow_up_buttons?: FollowUpButtonPayload[];
    charts: ChartSpec[];
  }>;

export type StreamMessage =
  | MetaMessage
  | DeltaMessage
  | ArtifactMessage
  | ChartMessage
  | ToolMessage
  | NuggetMessage
  | PromptMessage
  | PlaybookMessage
  | FeaturedTypeMessage
  | ComponentMessage
  | FollowUpButtonsMessage
  | DoneMessage;

export type DeltaStreamCallbacks = Readonly<{
  onMeta?: (meta: MetaMessage) => void;
  onDelta?: (accumulated: string) => void;
  onArtifact?: (message: ArtifactMessage) => void;
  onChart?: (chart: ChartMessage) => void;
  onTool?: (tool: ToolMessage) => void;
  onNugget?: (nugget: NuggetMessage) => void;
  onPrompt?: (prompt: PromptMessage) => void;
  onPlaybook?: (playbook: PlaybookMessage) => void;
  onFeaturedType?: (featuredType: FeaturedTypeMessage) => void;
  onComponent?: (component: ComponentMessage) => void;
  onDone?: (done: DoneMessageWithIds) => void;
}>;
